// ไฟล์: frontend/src/pages/LibraryPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './LibraryPage.css';

const LibraryPage = ({ user }) => {
  const [libraryItems, setLibraryItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    status: 'saved',
    collection_name: 'General'
  });

  // ดึงข้อมูลคลังส่วนตัว
  useEffect(() => {
    const fetchLibrary = async () => {
      try {
        const response = await axios.get('/library');
        setLibraryItems(response.data.items);
        
        // สร้างรายการ collections ที่ไม่ซ้ำกัน
        const uniqueCollections = [
          ...new Set(response.data.items.map(item => item.collection_name))
        ];
        setCollections(uniqueCollections);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching library:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลคลังส่วนตัวได้',
        });
        setLoading(false);
      }
    };

    fetchLibrary();
  }, []);

  // กรองรายการตาม collection
  const filteredItems = activeCollection === 'all'
    ? libraryItems
    : libraryItems.filter(item => item.collection_name === activeCollection);

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // เปิดโมดอลแก้ไข
  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      status: item.status,
      collection_name: item.collection_name
    });
  };

  // ปิดโมดอลแก้ไข
  const closeEditModal = () => {
    setEditingItem(null);
    setFormData({
      status: 'saved',
      collection_name: 'General'
    });
  };

  // อัพเดทสถานะและหมวดหมู่
  const updateItem = async () => {
    try {
      await axios.put('/library/status', {
        id: editingItem.library_id,
        status: formData.status,
        collection_name: formData.collection_name
      });
      
      // อัพเดทข้อมูลในหน้าจอ
      const updatedItems = libraryItems.map(item => {
        if (item.library_id === editingItem.library_id) {
          return {
            ...item,
            status: formData.status,
            collection_name: formData.collection_name
          };
        }
        return item;
      });
      
      setLibraryItems(updatedItems);
      
      // อัพเดท collections ถ้ามีการเพิ่มหมวดหมู่ใหม่
      if (!collections.includes(formData.collection_name)) {
        setCollections([...collections, formData.collection_name]);
      }
      
      closeEditModal();
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ!',
        text: 'อัพเดทรายการเรียบร้อยแล้ว',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } catch (error) {
      console.error('Error updating library item:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถอัพเดทรายการได้',
      });
    }
  };

  // ลบรายการออกจากคลัง
  const removeFromLibrary = (id) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณต้องการลบรายการนี้ออกจากคลังส่วนตัวหรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/library/${id}`);
          
          // อัพเดทข้อมูลในหน้าจอ
          const updatedItems = libraryItems.filter(item => item.library_id !== id);
          setLibraryItems(updatedItems);
          
          Swal.fire({
            icon: 'success',
            title: 'สำเร็จ!',
            text: 'ลบรายการเรียบร้อยแล้ว',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
        } catch (error) {
          console.error('Error removing from library:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบรายการได้',
          });
        }
      }
    });
  };

  // รับชื่อ item type
  const getItemTypeName = (item) => {
    if (item.resource_id) return 'สื่อการสอน';
    if (item.innovation_id) return 'นวัตกรรม';
    if (item.article_id) return 'บทความ';
    if (item.news_id) return 'ข่าวสาร';
    return '';
  };

  // รับชื่อไอคอนตาม item type
  const getItemTypeIcon = (item) => {
    if (item.resource_id) return 'fas fa-book';
    if (item.innovation_id) return 'fas fa-lightbulb';
    if (item.article_id) return 'fas fa-pen-fancy';
    if (item.news_id) return 'fas fa-newspaper';
    return 'fas fa-file';
  };

  // รับลิงก์ของ item
  const getItemLink = (item) => {
    if (item.resource_id) return `/resources/${item.resource_id}`;
    if (item.innovation_id) return `/innovations/${item.innovation_id}`;
    if (item.article_id) return `/articles/${item.article_id}`;
    if (item.news_id) return `/news/${item.news_id}`;
    return '#';
  };

  // รับชื่อของ item
  const getItemTitle = (item) => {
    if (item.resource_id) return item.resource_title;
    if (item.innovation_id) return item.innovation_title;
    if (item.article_id) return item.article_title;
    if (item.news_id) return item.news_title;
    return '';
  };

  // รับคำอธิบายของ item
  const getItemDescription = (item) => {
    if (item.resource_id) return item.resource_description;
    if (item.innovation_id) return item.innovation_description;
    if (item.article_id) return item.article_content;
    if (item.news_id) return item.news_content;
    return '';
  };

  // รับรูปภาพของ item
  const getItemThumbnail = (item) => {
    let thumbnail = '/assets/default-thumbnail.jpg';
    
    if (item.resource_id && item.resource_thumbnail) {
      thumbnail = `${process.env.REACT_APP_API_URL}/uploads/${item.resource_thumbnail}`;
    } else if (item.innovation_id && item.innovation_thumbnail) {
      thumbnail = `${process.env.REACT_APP_API_URL}/uploads/${item.innovation_thumbnail}`;
    } else if (item.article_id && item.article_thumbnail) {
      thumbnail = `${process.env.REACT_APP_API_URL}/uploads/${item.article_thumbnail}`;
    } else if (item.news_id && item.news_thumbnail) {
      thumbnail = `${process.env.REACT_APP_API_URL}/uploads/${item.news_thumbnail}`;
    }
    
    return thumbnail;
  };

  // รับแบดจ์สถานะ
  const getStatusBadge = (status) => {
    switch (status) {
      case 'saved':
        return <span className="badge badge-primary">บันทึกแล้ว</span>;
      case 'learned':
        return <span className="badge badge-success">เรียนรู้แล้ว</span>;
      case 'later':
        return <span className="badge badge-warning">ดูภายหลัง</span>;
      default:
        return <span className="badge badge-primary">บันทึกแล้ว</span>;
    }
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="loading-container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  return (
    <div className="library-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-bookmark"></i> คลังส่วนตัวของฉัน
          </h1>
          <p className="page-subtitle">รวบรวมสื่อการสอน นวัตกรรม บทความ และข่าวสารที่คุณบันทึกไว้</p>
        </div>

        {libraryItems.length === 0 ? (
          <div className="empty-library">
            <div className="empty-icon">
              <i className="fas fa-bookmark"></i>
            </div>
            <h3>คลังส่วนตัวของคุณว่างเปล่า</h3>
            <p>คุณยังไม่ได้บันทึกรายการใดๆ ลองสำรวจสื่อการสอน นวัตกรรม บทความ หรือข่าวสารแล้วบันทึกสิ่งที่น่าสนใจไว้ในคลังส่วนตัว</p>
            <div className="empty-actions">
              <Link to="/resources" className="btn btn-primary">
                <i className="fas fa-search"></i> ค้นหาสื่อการสอน
              </Link>
              <Link to="/innovations" className="btn btn-light">
                <i className="fas fa-lightbulb"></i> ดูนวัตกรรม
              </Link>
              <Link to="/articles" className="btn btn-light">
                <i className="fas fa-pen-fancy"></i> อ่านบทความ
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="library-tabs">
              <button
                className={`library-tab ${activeCollection === 'all' ? 'active' : ''}`}
                onClick={() => setActiveCollection('all')}
              >
                <i className="fas fa-th-large"></i> ทั้งหมด
                <span className="tab-count">{libraryItems.length}</span>
              </button>
              
              {collections.map((collection, index) => (
                <button
                  key={index}
                  className={`library-tab ${activeCollection === collection ? 'active' : ''}`}
                  onClick={() => setActiveCollection(collection)}
                >
                  <i className="fas fa-folder"></i> {collection}
                  <span className="tab-count">
                    {libraryItems.filter(item => item.collection_name === collection).length}
                  </span>
                </button>
              ))}
            </div>

            <div className="library-grid">
              {filteredItems.map((item) => (
                <div className="library-card" key={item.library_id}>
                  <div className="library-thumbnail">
                    <img src={getItemThumbnail(item)} alt={getItemTitle(item)} />
                    <div className="library-type">
                      <i className={getItemTypeIcon(item)}></i> {getItemTypeName(item)}
                    </div>
                  </div>
                  
                  <div className="library-content">
                    <h3 className="library-title">
                      <Link to={getItemLink(item)}>{getItemTitle(item)}</Link>
                    </h3>
                    
                    <div className="library-description">
                      {getItemDescription(item)?.length > 100
                        ? `${getItemDescription(item).substring(0, 100)}...`
                        : getItemDescription(item)}
                    </div>
                    
                    <div className="library-meta">
                      <div className="library-collection">
                        <i className="fas fa-folder"></i> {item.collection_name}
                      </div>
                      <div className="library-status">
                        {getStatusBadge(item.status)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="library-actions">
                    <Link to={getItemLink(item)} className="btn btn-sm btn-light">
                      <i className="fas fa-eye"></i> ดู
                    </Link>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => openEditModal(item)}
                    >
                      <i className="fas fa-edit"></i> แก้ไข
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => removeFromLibrary(item.library_id)}
                    >
                      <i className="fas fa-trash"></i> ลบ
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal แก้ไขรายการ */}
      {editingItem && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>แก้ไขรายการในคลัง</h3>
              <button className="modal-close" onClick={closeEditModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-item">
                <div className="modal-item-thumbnail">
                  <img src={getItemThumbnail(editingItem)} alt={getItemTitle(editingItem)} />
                </div>
                <div className="modal-item-info">
                  <h4>{getItemTitle(editingItem)}</h4>
                  <p>{getItemTypeName(editingItem)}</p>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="status" className="form-label">สถานะ</label>
                <select
                  id="status"
                  name="status"
                  className="form-select"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="saved">บันทึกแล้ว</option>
                  <option value="learned">เรียนรู้แล้ว</option>
                  <option value="later">ดูภายหลัง</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="collection_name" className="form-label">หมวดหมู่</label>
                <input
                  type="text"
                  id="collection_name"
                  name="collection_name"
                  className="form-control"
                  value={formData.collection_name}
                  onChange={handleChange}
                  placeholder="ชื่อหมวดหมู่"
                />
                <small className="text-muted">คุณสามารถสร้างหมวดหมู่ใหม่ได้โดยการพิมพ์ชื่อหมวดหมู่</small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-light" onClick={closeEditModal}>
                ยกเลิก
              </button>
              <button className="btn btn-primary" onClick={updateItem}>
                <i className="fas fa-save"></i> บันทึก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryPage;
